import { onMounted, onUnmounted, ref } from 'vue'

import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { useAppStore } from '@/store/app'

// Create a shared reactive state outside the composable function
const shortcuts = ref<KeyboardShortcut[]>([])
let isInitialized = false

type KeyboardShortcut = {
  ctrlKey: boolean
  shiftKey: boolean
  key: string
  callback?: () => void
}

/**
 * This composable is used to register keyboard shortcuts for the app.
 *
 * @returns An object with the registerSidebarToggleKeyboardEvent, registerNewChatKeyboardEvent, and initKeyboardEvents functions.
 */
export const useEventListeners = () => {
  const router = useRouter()
  const { isMac } = storeToRefs(useAppStore())

  const registerKeyboardShortcut = (shortcut: KeyboardShortcut) => {
    const isDuplicate = shortcuts.value.some(
      (existing) =>
        existing.ctrlKey === shortcut.ctrlKey &&
        existing.shiftKey === shortcut.shiftKey &&
        existing.key === shortcut.key,
    )
    if (!isDuplicate) {
      shortcuts.value.push(shortcut)
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // Access the current value of shortcuts.value at the time the event is triggered
    for (const shortcut of shortcuts.value) {
      const modifierKey = isMac.value ? event.metaKey : event.ctrlKey
      if (
        modifierKey === shortcut.ctrlKey &&
        event.shiftKey === shortcut.shiftKey &&
        event.key.toLowerCase() === shortcut.key.toLowerCase()
      ) {
        event.preventDefault()
        shortcut.callback?.()
        return
      }
    }
  }

  // Initialize a single global event listener for all keyboard shortcuts
  const initKeyboardEvents = () => {
    onMounted(() => {
      if (!isInitialized) {
        window.addEventListener('keydown', handleKeyDown)
        isInitialized = true
      }
    })

    onUnmounted(() => {
      // Only remove the event listener if we're completely unmounting the app
      // This ensures the event handler stays active across component changes
      if (isInitialized) {
        window.removeEventListener('keydown', handleKeyDown)
      }
    })
  }

  /**
   * Registers 'ctrl+s' keyboard event for toggling the sidebar
   */
  const registerSidebarToggleKeyboardEvent = (toggleFunc: () => void) => {
    registerKeyboardShortcut({
      ctrlKey: true,
      shiftKey: true,
      key: 's',
      callback: toggleFunc,
    })
  }

  /**
   * Registers 'ctrl+shift+o' (Windows) or 'cmd+shift+o' (Mac) keyboard event for starting a new chat
   */
  const registerNewChatKeyboardEvent = () => {
    registerKeyboardShortcut({
      ctrlKey: true,
      shiftKey: true,
      key: 'o',
      callback: () => router.push({ name: 'NewChat' }),
    })
  }

  return {
    registerSidebarToggleKeyboardEvent,
    registerNewChatKeyboardEvent,
    initKeyboardEvents,
  }
}
